// Utility function to get local date string without timezone conversion
export const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility function to safely parse date strings and maintain local timezone
export const parseLocalDate = (dateString) => {
  // Parse as local date (avoid timezone conversion)
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Environment-driven scheduling configuration (client-side)
const PAY_PERIOD_ANCHOR_MONTH = parseInt(process.env.REACT_APP_PAY_PERIOD_ANCHOR_MONTH || '6', 10); // 1-12
const PAY_PERIOD_ANCHOR_DAY = parseInt(process.env.REACT_APP_PAY_PERIOD_ANCHOR_DAY || '17', 10);
const PAY_PERIOD_LENGTH_DAYS = parseInt(process.env.REACT_APP_PAY_PERIOD_LENGTH_DAYS || '14', 10);
const STICK_LENGTH_DAYS = parseInt(process.env.REACT_APP_STICK_LENGTH_DAYS || '7', 10);
const PAY_PERIOD_START_NUMBER = parseInt(process.env.REACT_APP_PAY_PERIOD_START_NUMBER || '2', 10);
const VALID_PAY_PERIOD_MIN = parseInt(process.env.REACT_APP_VALID_PAY_PERIOD_MIN || '1', 10);
const VALID_PAY_PERIOD_MAX = parseInt(process.env.REACT_APP_VALID_PAY_PERIOD_MAX || '10', 10);

// Pay period utilities
export const getPayPeriodInfo = (date) => {
  // Pay period anchor defaults to June 17 of the current year
  const currentYear = new Date().getFullYear();
  const payPeriodAnchor = new Date(currentYear, (PAY_PERIOD_ANCHOR_MONTH - 1), PAY_PERIOD_ANCHOR_DAY);
  
  // Handle both string and Date inputs
  let inputDate;
  if (typeof date === 'string') {
    // Parse string dates in local timezone to avoid UTC conversion
    const [year, month, day] = date.split('-').map(Number);
    inputDate = new Date(year, month - 1, day);
  } else {
    inputDate = new Date(date);
  }
  
  // Normalize both dates to avoid timezone issues
  payPeriodAnchor.setHours(0, 0, 0, 0);
  inputDate.setHours(0, 0, 0, 0);
  
  // Calculate days since the start of the configured pay period anchor
  const daysDiff = Math.floor((inputDate - payPeriodAnchor) / (1000 * 60 * 60 * 24));
  
  // Each pay period length and numbering are configurable
  // Adjust for 0-based daysDiff: anchor day = day 0
  const payPeriodNumber = Math.floor(daysDiff / PAY_PERIOD_LENGTH_DAYS) + PAY_PERIOD_START_NUMBER;
  const dayInPayPeriod = ((daysDiff % PAY_PERIOD_LENGTH_DAYS) + PAY_PERIOD_LENGTH_DAYS) % PAY_PERIOD_LENGTH_DAYS;
  
  // Determine stick by configured stick length
  const stickNumber = Math.floor(dayInPayPeriod / STICK_LENGTH_DAYS) + 1;
  const dayInStick = dayInPayPeriod % STICK_LENGTH_DAYS;
  
  return {
    payPeriodNumber,
    stickNumber,
    dayInStick: dayInStick + 1, // Make it 1-based  
    dayInPayPeriod: dayInPayPeriod + 1, // Make it 1-based
    isValidPayPeriod: payPeriodNumber >= VALID_PAY_PERIOD_MIN && payPeriodNumber <= VALID_PAY_PERIOD_MAX
  };
};

export const getStickDates = (date) => {
  const payPeriodInfo = getPayPeriodInfo(date);
  
  // Handle both string and Date inputs
  let inputDate;
  if (typeof date === 'string') {
    inputDate = parseLocalDate(date);
  } else {
    inputDate = new Date(date);
  }
  
  // Calculate the start of the current stick based on pay period anchor
  // Use the same date as in getPayPeriodInfo for consistency
  const currentYear = new Date().getFullYear();
  const payPeriodAnchor = new Date(currentYear, (PAY_PERIOD_ANCHOR_MONTH - 1), PAY_PERIOD_ANCHOR_DAY);
  payPeriodAnchor.setHours(0, 0, 0, 0);
  inputDate.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((inputDate - payPeriodAnchor) / (1000 * 60 * 60 * 24));
  const payPeriodOffset = Math.floor(daysDiff / PAY_PERIOD_LENGTH_DAYS) * PAY_PERIOD_LENGTH_DAYS; // Get to start of current pay period
  const dayInPayPeriod = ((daysDiff % PAY_PERIOD_LENGTH_DAYS) + PAY_PERIOD_LENGTH_DAYS) % PAY_PERIOD_LENGTH_DAYS;
  
  // Determine which stick we're in and calculate stick start
  const stickNumber = Math.floor(dayInPayPeriod / STICK_LENGTH_DAYS) + 1;
  const stickStartOffset = payPeriodOffset + ((stickNumber - 1) * STICK_LENGTH_DAYS);
  
  const stickStart = new Date(payPeriodAnchor);
  stickStart.setDate(payPeriodAnchor.getDate() + stickStartOffset);
  
  // Generate all days in the stick
  const stickDates = [];
  for (let i = 0; i < STICK_LENGTH_DAYS; i++) {
    const stickDate = new Date(stickStart);
    stickDate.setDate(stickStart.getDate() + i);
    stickDates.push(getLocalDateString(stickDate));
  }
  
  return stickDates;
};

export const countAvailableDaysInStick = (date, scheduleData) => {
  const stickDates = getStickDates(date);
  
  return stickDates.reduce((count, stickDate) => {
    const dayData = scheduleData.find(d => d.date === stickDate);
    return count + (dayData && dayData.is_available === true ? 1 : 0);
  }, 0);
};

// Get today's date as YYYY-MM-DD string (timezone-safe)
export const getTodayString = () => {
  const today = new Date();
  return getLocalDateString(today);
};

// Add days to a date (timezone-safe)
export const addDays = (date, days) => {
  let baseDate;
  if (typeof date === 'string') {
    baseDate = parseLocalDate(date);
  } else {
    baseDate = new Date(date);
  }
  
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const day = baseDate.getDate();
  
  const newDate = new Date(year, month, day + days);
  return getLocalDateString(newDate);
};

// Get tomorrow's date as YYYY-MM-DD string (timezone-safe)
export const getTomorrowString = () => {
  return addDays(new Date(), 1);
};

// Get yesterday's date as YYYY-MM-DD string (timezone-safe)
export const getYesterdayString = () => {
  return addDays(new Date(), -1);
}; 